/**
 * Pure status-derivation helpers for the status page.
 *
 * Extracted from `App.tsx` so the precedence ladder, status→tone/label
 * mappings, and date formatting can be unit-tested without rendering the
 * React tree. No DOM, no module state — every function is a pure
 * transform of its arguments.
 */

export type ComponentStatus =
  | 'operational'
  | 'degraded'
  | 'partial-outage'
  | 'major-outage'
  | 'maintenance';

export type Tone = 'ok' | 'warn' | 'bad' | 'info';

/**
 * Roll a list of component statuses up to a single banner verdict.
 * Severity precedence (worst wins): major-outage > partial-outage >
 * degraded > maintenance > all-operational.
 */
export function computeOverall(components: { status: ComponentStatus }[]): {
  label: string;
  tone: Tone;
} {
  if (components.some((c) => c.status === 'major-outage')) {
    return { label: 'Major outage', tone: 'bad' };
  }
  if (components.some((c) => c.status === 'partial-outage')) {
    return { label: 'Partial outage', tone: 'warn' };
  }
  if (components.some((c) => c.status === 'degraded')) {
    return { label: 'Degraded performance', tone: 'warn' };
  }
  if (components.some((c) => c.status === 'maintenance')) {
    return { label: 'Scheduled maintenance', tone: 'info' };
  }
  return { label: 'All systems operational', tone: 'ok' };
}

export function toneFor(s: ComponentStatus): Tone {
  switch (s) {
    case 'operational':
      return 'ok';
    case 'degraded':
    case 'partial-outage':
      return 'warn';
    case 'major-outage':
      return 'bad';
    case 'maintenance':
      return 'info';
  }
}

export function labelFor(s: ComponentStatus): string {
  switch (s) {
    case 'operational':
      return 'Operational';
    case 'degraded':
      return 'Degraded';
    case 'partial-outage':
      return 'Partial outage';
    case 'major-outage':
      return 'Major outage';
    case 'maintenance':
      return 'Maintenance';
  }
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
